import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SearchFacet } from '../../types/search';
import { useSearch } from './hooks/useSearch';
import SearchInput from './components/SearchInput';
import SearchTabs from './components/SearchTabs';
import useIsMobile from './../hooks/is-mobile';
import SearchDesktopFilters from './components/SearchDesktopFilters';
import SearchPagination from './components/SearchPagination';
import SearchResultsContainer from './components/SearchResultsContainer';
import { useSearchConfig } from './hooks/useSearchConfig';
import React, { useState } from 'react';

import SearchNoResults from './components/SearchNoResults';
import LoadingSpinner from '../utils/LoadingSpinner';

const SearchInner = () => {
  const config = useSearchConfig();
  const isMobile = useIsMobile(1023);
  const [urlParams] = useState(new URLSearchParams(window.location.search));
  const [lastSearchedTerm, setLastSearchedTerm] = useState(urlParams.get('q') ?? '');

  const mountElement = document.getElementById('content-listing-root');
  const listingType = mountElement?.dataset.listingType ?? '';

  const {
    searchTerm,
    results,
    isLoading,
    isError,
    isSuccess,
    isFetching,
    facets,
    appliedFilters,
    pendingFilters,
    tabs,
    sortOptions,
    sortOrder,
    pagination,
    hasUnsearchedTerm,
    activeTabKey,
    searchQuery,

    // Setters
    setSearchTerm,

    // Actions
    toggleFacet,
    toggleFilter,
    handleTabClick,
    handleSearch: originalHandleSearch,
    handlePageChange,
    handleSortChange,
  } = useSearch(config.siteSearchApi, config.documentsOnly, config.paginationDisplayLimit);

  const handleSearch = () => {
    setLastSearchedTerm(searchTerm);
    originalHandleSearch();
  };

  // Filter facets by pagetype (activeTabKey)
  const filteredFacets: SearchFacet[] = facets
    .filter((facet: SearchFacet) => facet.pagetype === listingType || !facet.pagetype)
    .map((facet: SearchFacet) => ({
      ...facet,
      items: facet.items.filter(item => item.pagetype === listingType || !item.pagetype),
    }))
    .filter((facet: SearchFacet) => facet.items.some(item => item.total > 0));

  const filterProps = {
    facets: filteredFacets,
    appliedFacets: appliedFilters,
    pendingSelections: pendingFilters,
    onToggleFacet: toggleFacet,
    onFacetItemToggle: toggleFilter,
    labels: {
      applyFilters: config.labels.applyFiltersLabel,
      clearFilters: config.labels.clearFiltersLabel,
      showingLabel: config.labels.showingResultsForLabel,
      filtersLabel: config.labels.filtersLabel,
      closeLabel: config.labels.closeLabel,
      filterByLabel: config.labels.filterByLabel,
      sortByLabel: config.labels.sortByLabel,
      prevPageLabel: config.labels.prevPageLabel,
      nextPageLabel: config.labels.nextPageLabel,
    },
    sortOptions,
    sortOrder,
    setSortOrder: handleSortChange,
    onClose: () => {},
  };

  const renderSearchResults = () => {
    let content;

    if (isError) {
      content = (
        <SearchNoResults
          noResultsText={config.labels.noResultsText || 'An error occurred while searching. Please try again.'}
        />
      );
    } else if (isSuccess && results.length === 0 && !isLoading) {
      content = (
        <SearchNoResults noResultsText={config.labels.noResultsText?.replace('{query}', lastSearchedTerm) ?? ''} />
      );
    } else {
      content = (
        <SearchResultsContainer
          key={`search-results-container-${activeTabKey}`}
          results={results}
          searchTerm={searchTerm}
          lastSearchedTerm={lastSearchedTerm}
          hasSearched={lastSearchedTerm.trim() !== ''}
          noResultsText={config.labels.noResultsText}
          activeTabKey={activeTabKey}
          downloadLabel={config.labels.downloadLabel}
          lastUpdatedLabel={config.labels.lastUpdatedLabel}
          isLoading={isLoading}
        />
      );
    }

    return (
      <div className="search__results-container">
        {isFetching && <LoadingSpinner />}
        <div className="search__results-container-wrapper">{content}</div>
      </div>
    );
  };

  const shouldShowTabs = tabs.length > 0 && !config.documentsOnly;
  const shouldShowFiltersAndSort = !isLoading && isSuccess && results.length > 0;

  return (
    <>
      <div className="search__input-container">
        <div className="search__input-container-inner">
          <div className="search__input-wrapper">
            <SearchInput
              value={searchTerm}
              label={config.labels.showingResultsForLabel}
              placeholder="Type here to search"
              onChange={setSearchTerm}
              onSearch={handleSearch}
              isMobile={isMobile}
              buttonLabel={config.labels.searchCtaLabel}
              hasUnsearchedTerm={hasUnsearchedTerm}
            />
          </div>
        </div>
      </div>

      {shouldShowTabs && (
        <div className="search__tab-container">
          <div className="search__tab-wrapper">
            <SearchTabs
              tabs={tabs}
              onTabClick={handleTabClick}
              activeTabKey={activeTabKey}
              summary={searchQuery.data?.summary}
              isLoading={isFetching}
            />
          </div>
        </div>
      )}

      {shouldShowFiltersAndSort && (
        <div className="search__filters-sort-container">
          <div className="search__filters-sort-wrapper">
            <SearchDesktopFilters {...filterProps} />
          </div>
        </div>
      )}

      {renderSearchResults()}

      {pagination.totalPages > 1 && (
        <div className="search__pagination-container">
          <SearchPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            prevPageLabel={config.labels.prevPageLabel}
            nextPageLabel={config.labels.nextPageLabel}
          />
        </div>
      )}
    </>
  );
};

const searchQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export { searchQueryClient };

const Search = () => {
  return (
    <QueryClientProvider client={searchQueryClient}>
      <SearchInner />
    </QueryClientProvider>
  );
};

export default Search;
